<template>
  <div
    v-if="banner && banner.enabled"
    data-testid="app-notification-banner"
    class="app-notification-banner flex-shrink-0 mb-4"
  >
    <div
      class="banner-content d-flex flex-column flex-sm-row py-4 px-6"
      :class="expanded ? 'align-sm-start' : 'align-sm-center'"
    >
      <div
        data-testid="banner-main"
        class="d-flex"
        :class="expanded ? 'align-start' : 'align-center'"
      >
        <img
          data-testid="banner-robin-image"
          :src="robinImage"
          alt="Robin"
          class="banner-robin flex-shrink-0"
        />
        <div class="banner-text-container flex-grow-1 px-6 text-left">
          <Typography
            as="p"
            variant="body-1"
            data-testid="banner-collapsed-text"
            class="banner-message ma-0"
          >
            {{ banner.interpolateDate() }}
          </Typography>
          <v-expand-transition>
            <div v-if="expanded" data-testid="banner-expanded-content" class="mt-4 text-left">
              <ul class="banner-items pa-0 ma-0">
                <li v-for="(segment, index) in decoratedItems" :key="index" class="ma-0">
                  <Typography as="span" variant="body-1">
                    • {{ segment.before
                    }}<Icon
                      v-if="segment.icon"
                      :icon="segment.icon"
                      small
                      color=""
                      :data-testid="segment.testid"
                      class="banner-inline-icon"
                    />
                    {{ segment.rest }}
                  </Typography>
                </li>
              </ul>
              <Typography as="p" variant="body-1" class="banner-reassurance ma-0 mt-4">{{
                banner.reassuranceText
              }}</Typography>
            </div>
          </v-expand-transition>
        </div>
      </div>
      <Button
        v-if="hasDetail"
        data-testid="banner-toggle-btn"
        text
        small
        color="brandPrimary"
        :iconRight="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        :aria-expanded="expanded.toString()"
        @click="expanded = !expanded"
        class="banner-toggle-btn flex-shrink-0 align-self-end align-self-sm-auto"
      >
        <Typography as="span" variant="body-2">{{
          expanded ? "HIDE DETAILS" : "SHOW DETAILS"
        }}</Typography>
      </Button>
    </div>
  </div>
</template>

<script>
import robinImage from "@/assets/robin/robin1.png";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import Typography from "@/components/generic/Typography.vue";

/**
 * Keywords that get an inline icon, in match order. Data rather than template
 * branches so adding one does not mean duplicating the markup again.
 */
const ITEM_ICONS = [
  { keyword: "Dashboard", icon: "mdi-star-check", testid: "icon-dashboard" },
  { keyword: "Library", icon: "mdi-view-grid-plus", testid: "icon-library" },
];

export default {
  name: "AppNotificationBanner",
  components: { Button, Icon, Typography },
  props: {
    banner: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      expanded: false,
      robinImage,
    };
  },
  computed: {
    /**
     * Whether there is anything to expand into.
     *
     * Content-driven rather than notice-specific: the Programs-retirement
     * notice carries bullet detail and gets an expander, the domain-migration
     * notice is a single paragraph and gets none, and a future long
     * announcement gets one automatically just by authoring detail into the
     * flag payload. Without this the migration notice would render a control
     * that opens an empty region.
     *
     * `reassuranceText` covers `expandedText` too — the model falls the
     * former back to the latter when it is blank.
     */
    hasDetail() {
      const items = this.banner?.expandedItems ?? [];
      const reassurance = this.banner?.reassuranceText ?? "";
      return items.length > 0 || reassurance.trim().length > 0;
    },
    /**
     * Splits each item around the first icon keyword. Uses indexOf/slice
     * rather than split(keyword)[1], which drops everything past the second
     * occurrence when a keyword repeats within one item.
     */
    decoratedItems() {
      const items = this.banner?.expandedItems ?? [];
      return items.map((item) => {
        const match = ITEM_ICONS.find((candidate) => item.includes(candidate.keyword));
        if (!match) {
          return { before: item, icon: null, testid: null, rest: "" };
        }
        const at = item.indexOf(match.keyword);
        return {
          before: item.slice(0, at),
          icon: match.icon,
          testid: match.testid,
          rest: item.slice(at),
        };
      });
    },
  },
};
</script>

<style scoped>
/*
 * Per Daniel's standing direction: prefer Vue/Vuetify classes over custom
 * classes wherever possible. What remains here is only what has no utility
 * equivalent — exact pixel sizing, list-style, the hover overlay, and the
 * inherited text colour. The grey gap under the banner is `mb-4`.
 */
.app-notification-banner {
  background-color: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
}

/* padding -> py-4 px-6 */
.banner-content {
  gap: 0;
}

/* flex-shrink -> flex-shrink-0; exact Figma dimensions have no utility */
.banner-robin {
  height: 31px;
  width: 30px;
}

/* flex/padding/text-align -> flex-grow-1 px-6 text-left */
.banner-text-container {
  min-width: 0;
}

/* margin -> ma-0; font comes from Typography body-1 */
.banner-message {
  color: rgba(0, 0, 0, 0.87);
}

/*
 * Colour, radius, uppercase, letter-spacing, border and min-width all come
 * from Button/v-btn defaults; flex-shrink -> flex-shrink-0. Only the hover
 * overlay suppression has no utility equivalent.
 */
.banner-toggle-btn::before {
  background-color: transparent !important;
}

/* padding/margin -> pa-0 ma-0 */
.banner-items {
  list-style: none;
}

/* margin -> ma-0 on the li */
.banner-items li {
  color: rgba(0, 0, 0, 0.87);
}

/* 2px is off the 4px Vuetify spacing scale, so no utility applies. */
.banner-inline-icon {
  vertical-align: middle;
  margin: 0 2px;
}

/* margin -> ma-0 mt-4 */
.banner-reassurance {
  color: rgba(0, 0, 0, 0.87);
}
</style>
